import { useEffect, useRef, useCallback } from 'react';

// 90 minutes in milliseconds
const IDLE_TIMEOUT_MS = 90 * 60 * 1000;

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

interface UseIdleTimeoutOptions {
  /** Callback to execute when idle timeout is reached */
  onTimeout: () => void;
  /** Whether the timeout tracking is enabled */
  enabled?: boolean;
  /** Timeout duration in milliseconds (default: 90 minutes) */
  timeoutMs?: number;
}

/**
 * Tracks user inactivity and triggers a callback (e.g. logout) after a
 * specified timeout. Monitors mouse, keyboard, scroll, and touch events.
 */
export const useIdleTimeout = ({ onTimeout, enabled = true, timeoutMs = IDLE_TIMEOUT_MS }: UseIdleTimeoutOptions): void => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const clearIdleTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const startIdleTimeout = useCallback(() => {
    clearIdleTimeout();
    timeoutRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [clearIdleTimeout, timeoutMs]);

  const handleActivity = useCallback(() => {
    startIdleTimeout();
  }, [startIdleTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearIdleTimeout();
      return;
    }

    startIdleTimeout();
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearIdleTimeout();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, handleActivity, startIdleTimeout, clearIdleTimeout]);
};

export default useIdleTimeout;
